import React, { useState } from "react";
import { useAuth } from "./AuthContext";
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  CircularProgress,
  IconButton,
  InputAdornment,
  Divider,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Visibility, VisibilityOff, Email, Lock } from "@mui/icons-material";
import googleImg from "../assets/google.png";
import logoImg from "../assets/logo.png";
import loginIllustration from "../assets/login-image.jpg";
import colors from "../constants/color"; 
import { fetchSignInMethodsForEmail } from "firebase/auth";
import { auth } from "./firebase/firebase"; 

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSignUp, setIsSignUp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // New states for Google account password setup
  const [showPasswordSetup, setShowPasswordSetup] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [tempEmail, setTempEmail] = useState("");

  const { signup, signin, signInWithGoogle, linkPasswordToGoogleAccount } = useAuth();
  const theme = colors.dark;
  
  // Media queries for responsive design
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

 const handleSubmit = async (e) => {
  e.preventDefault();
  setError("");
  setLoading(true);

  console.log("Form submitted:", { email, isSignUp });

  try {
    if (isSignUp) {
      console.log("Attempting signup...");
      await signup(email.trim(), password);
      console.log("Signup successful");
    } else {
      console.log("Attempting signin...");
      await signin(email.trim(), password);
      console.log("Signin successful");
    }
  } catch (err) {
    console.log("Auth error:", err.code, err.message);

    // Check if email already exists during signup
    if (isSignUp && err.code === "auth/email-already-in-use") {
      console.log("Email already in use, checking sign-in methods...");
      try {
        const methods = await fetchSignInMethodsForEmail(auth, email.trim());
        console.log("Sign-in methods found:", methods);

        // If no methods found or Google method exists, offer password setup
        if (methods.length === 0 || methods.includes("google.com")) {
          console.log("Offering password setup for existing account");
          setTempEmail(email.trim());
          setShowPasswordSetup(true);
          setError("");
          setLoading(false);
          return;
        } else if (methods.includes("password")) {
          // If password already exists
          setError("Email already registered. Please sign in instead.");
          setLoading(false);
          return;
        }
      } catch (fetchErr) {
        console.error("Error fetching sign-in methods:", fetchErr);
        // If we can't fetch methods, still offer password setup as fallback
        setTempEmail(email.trim());
        setShowPasswordSetup(true);
        setError("");
        setLoading(false);
        return;
      }
    }

    // Other errors
    setError(
      err.code === "auth/wrong-password" || err.code === "auth/user-not-found"
        ? "Invalid email or password"
        : err.message || "Something went wrong"
    );
  }
  setLoading(false);
};

  const handleGoogleSignIn = async () => {
    setError("");
    setLoading(true);
    try {
      await signInWithGoogle();
    } catch (error) {
      setError(error.message);
    }
    setLoading(false);
  };

  // Handle setting password for existing Google account
  const handleSetPasswordForGoogleAccount = async () => {
    setError("");
    setLoading(true);

    console.log("Setting password for Google account:", tempEmail);

    try {
      // Validate passwords
      if (newPassword !== confirmPassword) {
        setError("Passwords do not match");
        setLoading(false);
        return;
      }

      const passwordError = validatePassword(newPassword);
      if (passwordError) {
        setError(passwordError);
        setLoading(false);
        return;
      }

      console.log("Linking password to Google account...");
      // Link password to Google account using the updated function
      await linkPasswordToGoogleAccount(tempEmail, newPassword);
      
      console.log("Password set successfully!");
      // Success - close dialog and show success message
      setShowPasswordSetup(false);
      setNewPassword("");
      setConfirmPassword("");
      setError("Password set successfully! You can now login with email/password or Google.");
      
    } catch (error) {
      console.error("Error setting password:", error);
      setError(error.message);
    }

    setLoading(false);
  };

  // Close password setup dialog
  const handleClosePasswordSetup = () => {
    setShowPasswordSetup(false);
    setNewPassword("");
    setConfirmPassword("");
    setError("");
  };

  const validatePassword = (password) => {
    if (password.length < 8)
      return "Password must be at least 8 characters.";

    if (!/[A-Z]/.test(password))
      return "Password must contain at least one uppercase letter.";

    if (!/[a-z]/.test(password))
      return "Password must contain at least one lowercase letter.";

    if (!/[0-9]/.test(password))
      return "Password must contain at least one number.";

    if (!/[!@#$%^&*]/.test(password))
      return "Password must contain at least one special character (!@#$%^&*).";

    return null; // valid
  };

  // Rest of your JSX remains the same...
  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        width: "100%",
        overflow: "hidden",
        margin: 0,
        padding: 0,
        position: "fixed",
        top: 0,
        left: 0,
        flexDirection: { xs: "column", md: "row" }, // Column on mobile, row on desktop
      }}
    >
      {/* LEFT SIDE - Brand / Image Section */}
      <Box
        sx={{
          flex: { xs: "0 0 auto", md: 1 }, // Auto height on mobile, flex 1 on desktop
          width: { xs: "100%", md: "auto" },
          height: { xs: "40vh", md: "100%" }, // 40% height on mobile, full on desktop
          backgroundColor: theme.sidebarBg,
          color: theme.textPrimary,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          position: "relative",
          backgroundImage: {
            xs: `linear-gradient(rgba(6, 14, 44, 0.9), rgba(6, 14, 44, 0.9)), url(${loginIllustration})`,
            md: `linear-gradient(rgba(6, 14, 44, 0.8), rgba(6, 14, 44, 0.8)), url(${loginIllustration})`
          },
          backgroundSize: "cover",
          backgroundPosition: "center",
          px: { xs: 2, sm: 3, md: 6 },
          py: { xs: 3, md: 0 },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: {
              xs: "linear-gradient(135deg, rgba(6,14,44,0.98) 20%, rgba(0,0,0,0.4) 100%)",
              md: "linear-gradient(135deg, rgba(6,14,44,0.95) 20%, rgba(0,0,0,0.4) 100%)"
            },
          }}
        />

        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            width: "100%",
            maxWidth: { xs: "100%", md: "90%", lg: "80%" },
          }}
        >
          <Box
            component="img"
            src={logoImg}
            alt="LeadsToSell"
            sx={{
              height: { xs: 50, sm: 60, md: 70 },
              mb: { xs: 2, md: 3 },
              filter: "drop-shadow(0 3px 8px rgba(0,0,0,0.4))",
            }}
          />

          <Typography 
            variant="h4" 
            fontWeight="700" 
            mb={2}
            sx={{
              fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem", lg: "2.25rem" },
              px: { xs: 1, sm: 0 }
            }}
          >
            Welcome to LeadsToSell
          </Typography>

          <Typography
            variant="body1"
            sx={{
              maxWidth: { xs: "100%", sm: 400, md: 450 },
              color: theme.textSecondary,
              lineHeight: 1.7,
              mb: { xs: 2, md: 3 },
              fontSize: { xs: "0.9rem", sm: "1rem" },
              px: { xs: 1, sm: 0 },
              display: { xs: "none", sm: "block" } // Hide on very small mobile
            }}
          >
            Manage leads, track growth, and close deals faster — all in one
            powerful dashboard.
          </Typography>

          <Box
            component="img"
            src={loginIllustration}
            alt="Sales Illustration"
            sx={{
              height: { xs: 120, sm: 160, md: 200, lg: 280 },
              width: "auto",
              borderRadius: 3,
              mt: { xs: 1, md: 3 },
              display: { xs: "none", sm: "none", md: "block" }, // Only show on desktop
              boxShadow: theme.shadow,
              maxWidth: "90%",
            }}
            onError={(e) => (e.target.style.display = "none")}
          />
        </Box>
      </Box>

      {/* RIGHT SIDE - Login Form */}
      <Box
        sx={{
          flex: { xs: 1, md: 1 },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: colors.white,
          px: { xs: 2, sm: 3, md: 4, lg: 6 },
          py: { xs: 3, sm: 4, md: 0 },
          overflow: "auto",
          minHeight: { xs: "60vh", md: "100%" },
        }}
      >
        <Card
          sx={{
            width: "100%",
            maxWidth: { xs: "100%", sm: 400, md: 420, lg: 450 },
            borderRadius: { xs: 3, md: 4 },
            backgroundColor: colors.white,
            boxShadow: {
              xs: "0 4px 12px rgba(0, 0, 0, 0.08)",
              sm: "0 8px 20px rgba(0, 0, 0, 0.08), 0 16px 40px rgba(0, 0, 0, 0.06)",
            },
            border: `1px solid rgba(0, 0, 0, 0.05)`,
            transition: "all 0.3s ease",
            "&:hover": {
              transform: { xs: "none", sm: "translateY(-5px)" },
              boxShadow: {
                xs: "0 4px 12px rgba(0, 0, 0, 0.08)",
                sm: "0 12px 28px rgba(0, 0, 0, 0.12), 0 20px 45px rgba(0, 0, 0, 0.10)",
              },
            },
          }}
        >
          {/* HEADER STRIP */}
          <Box
            sx={{
              textAlign: "center",
              py: { xs: 2.5, md: 3 },
              background: theme.sidebarBg,
              borderTopLeftRadius: { xs: 12, md: 16 },
              borderTopRightRadius: { xs: 12, md: 16 },
            }}
          >
            <Box
              component="img"
              src={logoImg}
              alt="LeadsToSell"
              sx={{
                height: { xs: 35, md: 40 },
                width: "auto",
                mb: { xs: 1, md: 1.2 },
              }}
            />
            <Typography
              variant="body2"
              sx={{
                color: colors.white,
                fontSize: { xs: "0.8rem", md: "0.875rem" },
              }}
            >
              {isSignUp ? "Create your account" : "Welcome back to portal"}
            </Typography>
          </Box>

          <CardContent sx={{ 
            p: { xs: 3, sm: 4 },
            '&:last-child': { 
              pb: { xs: 3, sm: 4 } 
            } 
          }}>
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: { xs: 1.5, sm: 2 },
              }}
            >
              {/* Email */}
              <TextField
                label="Email Address"
                type="email"
                fullWidth
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email sx={{ 
                        color: theme.iconDefault,
                        fontSize: { xs: "1.1rem", sm: "1.25rem" } 
                      }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  input: { 
                    color: "#000",
                    fontSize: { xs: "0.9rem", sm: "1rem" },
                    py: { xs: 1.2, sm: 1.4 }
                  },
                  label: { 
                    color: "#555",
                    fontSize: { xs: "0.9rem", sm: "1rem" }
                  },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: { xs: 1.5, sm: 2 },
                    "& fieldset": { borderColor: "#ddd" },
                    "&:hover fieldset": { borderColor: theme.accent },
                    "&.Mui-focused fieldset": {
                      borderColor: theme.accent,
                      borderWidth: 2,
                    },
                  },
                }}
              />

              {/* Password */}
              <TextField
                label="Password"
                type={showPassword ? "text" : "password"}
                fullWidth
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock sx={{ 
                        color: theme.iconDefault,
                        fontSize: { xs: "1.1rem", sm: "1.25rem" } 
                      }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                        sx={{ color: theme.iconDefault }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  input: { 
                    color: "#000",
                    fontSize: { xs: "0.9rem", sm: "1rem" },
                    py: { xs: 1.2, sm: 1.4 }
                  },
                  label: { 
                    color: "#555",
                    fontSize: { xs: "0.9rem", sm: "1rem" }
                  },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: { xs: 1.5, sm: 2 },
                    "& fieldset": { borderColor: "#ddd" },
                    "&:hover fieldset": { borderColor: theme.accent },
                    "&.Mui-focused fieldset": {
                      borderColor: theme.accent,
                      borderWidth: 2,
                    },
                  },
                }}
              />

              {/* Error */}
              {error && (
                <Box
                  sx={{
                    backgroundColor: error.includes("successfully") 
                      ? "rgba(0,255,0,0.1)" 
                      : "rgba(255,0,0,0.1)",
                    border: error.includes("successfully")
                      ? `1px solid rgba(0,255,0,0.2)`
                      : `1px solid rgba(255,0,0,0.2)`,
                    borderRadius: { xs: 1.5, sm: 2 },
                    p: { xs: 1, sm: 1.2 },
                    textAlign: "center",
                  }}
                >
                  <Typography 
                    color={error.includes("successfully") ? "success" : "error"} 
                    variant="body2" 
                    fontWeight="500"
                    sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
                  >
                    {error}
                  </Typography>
                </Box>
              )}

              {/* Submit */}
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={loading}
                sx={{
                  py: { xs: 1.1, sm: 1.3 },
                  borderRadius: { xs: 1.5, sm: 2 },
                  fontWeight: "bold",
                  textTransform: "none",
                  background: theme.sidebarBg,
                  color: "#fff",
                  boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                  "&:hover": {
                    background: theme.sidebarBg,
                    transform: { xs: "none", sm: "translateY(-1px)" },
                    boxShadow: { 
                      xs: "0 4px 10px rgba(0,0,0,0.2)",
                      sm: "0 6px 18px rgba(0,0,0,0.25)" 
                    },
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : isSignUp ? (
                  "Create Account"
                ) : (
                  "Sign In"
                )}
              </Button>

              {/* Divider */}
              <Box sx={{ 
                position: "relative", 
                textAlign: "center", 
                my: { xs: 1.5, sm: 2 } 
              }}>
                <Divider sx={{ borderColor: "#ccc" }} />
                <Typography
                  variant="body2"
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    backgroundColor: colors.white,
                    px: 2,
                    color: "#777",
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  }}
                >
                  or continue with
                </Typography>
              </Box>

              {/* Google */}
              <Button
                variant="outlined"
                fullWidth
                onClick={handleGoogleSignIn}
                disabled={loading}
                sx={{
                  py: { xs: 1, sm: 1.1 },
                  borderRadius: { xs: 1.5, sm: 2 },
                  borderColor: "#ccc",
                  color: "#000",
                  fontWeight: 500,
                  textTransform: "none",
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                  "&:hover": {
                    borderColor: theme.accent,
                    backgroundColor: theme.accentLight,
                  },
                }}
              >
                <Box
                  component="img"
                  src={googleImg}
                  alt="Google"
                  sx={{ 
                    height: { xs: 18, sm: 20 }, 
                    width: { xs: 18, sm: 20 }, 
                    mr: { xs: 1, sm: 1.5 } 
                  }}
                />
                Sign in with Google
              </Button>

              {/* Toggle */}
              <Typography
                align="center"
                variant="body2"
                sx={{ 
                  mt: { xs: 1.5, sm: 2 }, 
                  color: "#555",
                  fontSize: { xs: "0.8rem", sm: "0.875rem" }
                }}
              >
                {isSignUp ? (
                  <>
                    Already have an account?{" "}
                    <Button
                      variant="text"
                      onClick={() => setIsSignUp(false)}
                      sx={{
                        textTransform: "none",
                        fontWeight: "bold",
                        color: theme.accent,
                        fontSize: { xs: "0.8rem", sm: "0.875rem" },
                        minWidth: "auto",
                        p: 0.5,
                      }}
                    >
                      Sign In
                    </Button>
                  </>
                ) : (
                  <>
                    Don't have an account?{" "}
                    <Button
                      variant="text"
                      onClick={() => setIsSignUp(true)}
                      sx={{
                        textTransform: "none",
                        fontWeight: "bold",
                        color: theme.accent,
                        fontSize: { xs: "0.8rem", sm: "0.875rem" },
                        minWidth: "auto",
                        p: 0.5,
                      }}
                    >
                      Sign Up
                    </Button>
                  </>
                )}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Password Setup Dialog for Existing Google Accounts */}
      <Dialog 
        open={showPasswordSetup} 
        onClose={handleClosePasswordSetup}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          background: colors.white,
          color: theme.sidebarBg,
          textAlign: "center",
          borderBottom:`1px solid ${theme.sidebarBg}`
        }}>
          Set Password for Your Account
        </DialogTitle>
        <DialogContent sx={{ mt: 2, textAlign: "center" }}>
          <Typography variant="body1" fontWeight="600" mb={1}>
            Google Account Detected
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            The email <strong>{tempEmail}</strong> is already connected via Google Sign-In.<br />
            To enable email/password login, set a password below.
          </Typography>

          <TextField
            label="New Password"
            type={showNewPassword ? "text" : "password"}
            fullWidth
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Lock /></InputAdornment>,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowNewPassword(!showNewPassword)}>
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          <TextField
            label="Confirm Password"
            type={showNewPassword ? "text" : "password"}
            fullWidth
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Lock /></InputAdornment>,
            }}
            sx={{ mb: 2 }}
          />

          {error && (
            <Typography color="error" variant="body2" mt={1}>
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, flexDirection: 'column' }}>
          <Button onClick={handleClosePasswordSetup}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSetPasswordForGoogleAccount}
            disabled={loading || newPassword !== confirmPassword || newPassword.length < 8}
            fullWidth
            sx={{ mt: 2, py: 1.5 }}
          >
            {loading ? <CircularProgress size={24} /> : "Set Password"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Login;